/**
 * Archimedes' principle: a body in a fluid is pushed up by a force equal to the weight of
 * the fluid it displaces.
 *
 *   F_b = ρ_fluid · V_displaced · g
 *
 * A floating object displaces just enough fluid to balance its own weight, so the
 * submerged fraction is ρ_object / ρ_fluid; one denser than the fluid sinks, and its
 * apparent weight underwater is W − F_b. SI units.
 */

export type BuoyancyError = "missing" | "non-positive" | "not-finite";

interface Failure {
  ok: false;
  error: BuoyancyError;
}

function positive(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

/* ── F = ρ V g ────────────────────────────────────────────────────────────── */

export type ArchimedesTarget = "force" | "volume" | "density";

export interface ArchimedesInputs {
  force: number | null;
  volume: number | null;
  density: number | null;
  gravity: number | null;
}

export interface ArchimedesSolution {
  force: number;
  volume: number;
  density: number;
  gravity: number;
  /** Mass of the displaced fluid ρV, kg. */
  displacedMass: number;
}

const NEEDS: Record<ArchimedesTarget, readonly (keyof ArchimedesInputs)[]> = {
  force: ["volume", "density", "gravity"],
  volume: ["force", "density", "gravity"],
  density: ["force", "volume", "gravity"],
};

export function solveArchimedes(
  inputs: ArchimedesInputs,
  target: ArchimedesTarget
): { ok: true; value: ArchimedesSolution } | Failure {
  for (const key of NEEDS[target]) {
    if (inputs[key] === null) {
      return { ok: false, error: "missing" };
    }
    if (!positive(inputs[key])) {
      return { ok: false, error: "non-positive" };
    }
  }
  let { force, volume, density } = {
    force: inputs.force ?? 0,
    volume: inputs.volume ?? 0,
    density: inputs.density ?? 0,
  };
  const gravity = inputs.gravity ?? 0;
  if (target === "force") {
    force = density * volume * gravity;
  } else if (target === "volume") {
    volume = force / (density * gravity);
  } else {
    density = force / (volume * gravity);
  }
  const solution = {
    force,
    volume,
    density,
    gravity,
    displacedMass: density * volume,
  };
  if (!Object.values(solution).every((v) => Number.isFinite(v) && v > 0)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/* ── Will it float? ───────────────────────────────────────────────────────── */

export type FloatState = "floats" | "sinks" | "neutral";

export interface FloatInputs {
  /** The object's density, kg/m³ — or null to derive it from `mass`. */
  objectDensity: number | null;
  /** The object's mass, kg — used when no density is given. */
  mass: number | null;
  volume: number | null;
  fluidDensity: number | null;
  gravity: number | null;
}

export interface FloatSolution {
  state: FloatState;
  objectDensity: number;
  mass: number;
  volume: number;
  fluidDensity: number;
  gravity: number;
  /** Weight in air (strictly, in vacuum) mg, N. */
  weight: number;
  /** Buoyant force if fully submerged, ρ_fluid V g, N. */
  maxBuoyancy: number;
  /** Buoyant force actually acting: the weight if it floats, F_max if it sinks. */
  buoyantForce: number;
  /** Fraction of the volume below the surface (1 when it sinks). */
  fractionSubmerged: number;
  submergedVolume: number;
  /** Weight minus F_max: the apparent weight when held fully underwater. Negative = it
   *  would rise, and this is the hold-down force needed. */
  apparentWeightSubmerged: number;
  /** Extra weight a floating object can take before sinking (0 if it already sinks). */
  reserve: number;
}

const NEUTRAL_TOLERANCE = 1e-9;

export function solveFloat(
  inputs: FloatInputs
): { ok: true; value: FloatSolution } | Failure {
  const { objectDensity, mass, volume, fluidDensity, gravity } = inputs;
  if (volume === null || fluidDensity === null || gravity === null) {
    return { ok: false, error: "missing" };
  }
  if (objectDensity === null && mass === null) {
    return { ok: false, error: "missing" };
  }
  if (
    !(positive(volume) && positive(fluidDensity) && positive(gravity)) ||
    (objectDensity !== null && !positive(objectDensity)) ||
    (objectDensity === null && !positive(mass))
  ) {
    return { ok: false, error: "non-positive" };
  }
  const density = objectDensity ?? (mass as number) / volume;
  const bodyMass = objectDensity === null ? (mass as number) : density * volume;
  const weight = bodyMass * gravity;
  const maxBuoyancy = fluidDensity * volume * gravity;
  const ratio = density / fluidDensity;
  let state: FloatState = "floats";
  if (Math.abs(ratio - 1) <= NEUTRAL_TOLERANCE) {
    state = "neutral";
  } else if (ratio > 1) {
    state = "sinks";
  }
  const fraction = Math.min(ratio, 1);
  const solution: FloatSolution = {
    state,
    objectDensity: density,
    mass: bodyMass,
    volume,
    fluidDensity,
    gravity,
    weight,
    maxBuoyancy,
    buoyantForce: state === "sinks" ? maxBuoyancy : weight,
    fractionSubmerged: fraction,
    submergedVolume: fraction * volume,
    apparentWeightSubmerged: weight - maxBuoyancy,
    reserve: state === "sinks" ? 0 : maxBuoyancy - weight,
  };
  const numbers = Object.values(solution).filter(
    (item): item is number => typeof item === "number"
  );
  if (!numbers.every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}
